import {
  FundingPolicyType,
  SessionType,
  isPolicyEligibleForApplication,
  zipCodeToState,
} from '@shared';
import { useDispatch, useSelector } from 'react-redux';
import { AnnuityDetailsModal, ExplainSlideButton, OnboardingChrome } from '../components';
import {
  selectClient,
  selectClientOnboardingSlideProgress,
  selectFeatureFlags,
  selectSession,
} from '../model';

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlideContent } from '../components/SlideContent';

import { CustomizePlanButtonGroup } from '@/components/CustomizePlanButtonGroup';
import { FundingTable } from '@/components/FundingTable';
import { Button } from '@/components/ui/button';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import {
  CLIENT_COMPLETED_REVIEW,
  navigateToPrintReport,
  putClientOnboardingSlideProgressByClientIdRequest,
} from '@client';
import { AddPolicyModal } from '@client/components/AddPolicyModal';
import { GroupInsurancePlanSelector } from '@client/components/GroupInsurancePlanSelector';
import { useShopInsuranceModalQueryParam } from '@client/hooks/useShopInsuranceModalQueryParam';
import { INSURANCE_SHOPPING_CONFIG } from '@client/util/constants';
import { calculateAgeFromBirthDate, newUuid } from '@shared';
import { Banknote, FileDown } from 'lucide-react';
import { GoPlus } from 'react-icons/go';
import { MdOutlineSavings } from 'react-icons/md';
import { PiSparkle } from 'react-icons/pi';
import { OnboardingFundingCoverage, citations } from '..';
import { AddInsuranceOptionsModal } from '../components/AddInsuranceOptionsModal';
import EmptyStateAddButtons, { EmptyStateAddOptionDef } from '../components/EmptyStateAddButtons';
import FundingSourceMetrics from '../components/FundingSourceMetrics';
import { SelectFundingSourceModal } from '../components/SelectFundingSourceModal';
import { SelfFundingDetailsModal } from '../components/SelfFundingDetailsModal';
import { ShopInsuranceModal } from '../components/ShopInsuranceModal';
import { OnboardingPolicyApplicationDashboard } from './OnboardingPolicyApplicationDashboard';

export type InsuranceState = {
  selectedPolicyType: FundingPolicyType | null;
  selectedPolicyFundingSourceId?: string;
  openInsuranceDetailsModal: boolean;
};

export function OnboardingFundingSources({ clientId }: ClientIdProps) {
  const client = useSelector(selectClient)!;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const onboardingSlideProgress = useSelector(selectClientOnboardingSlideProgress);
  const { clientFirstName, intakeSurvey } = client;
  const { sessionType } = useSelector(selectSession);

  const vbShopFeatureFlag = useSelector(selectFeatureFlags).vbShop;
  const agisFeatureFlag = useSelector(selectFeatureFlags).agis;
  const showGroupPolicy = vbShopFeatureFlag || agisFeatureFlag;
  const featureFlags = useFeatureFlags();

  const [insuranceState, setInsuranceState] = useState<InsuranceState>({
    selectedPolicyType: null,
    selectedPolicyFundingSourceId: undefined,
    openInsuranceDetailsModal: false,
  });
  const [selectedSelfFundingSourceId, setSelectedSelfFundingSourceId] = useState<
    string | undefined
  >(undefined);
  const [openSelfFundingModal, setOpenSelfFundingModal] = useState(false);
  const [openAnnuityDetailsModal, setOpenAnnuityDetailsModal] = useState(false);
  const [openSelectFundingSourceModal, setOpenSelectFundingSourceModal] = useState(false);
  const [openAddInsuranceOptionsModal, setOpenAddInsuranceOptionsModal] = useState(false);

  const {
    queryParam,
    openShopInsuranceModal,
    isQueryReady,
    suggestDownloadReportParam,
    handleOpenShopInsuranceModal,
    handleCloseShopInsuranceModal,
    handleShowDownloadReport,
  } = useShopInsuranceModalQueryParam();

  const isEligibleForShopInsurance = useMemo(() => {
    if (!intakeSurvey.clientBirthDate) return false;

    const age = calculateAgeFromBirthDate(intakeSurvey.clientBirthDate);
    return (
      age >= INSURANCE_SHOPPING_CONFIG.eligibilityAge.min &&
      age <= INSURANCE_SHOPPING_CONFIG.eligibilityAge.max
    );
  }, [intakeSurvey.clientBirthDate]);

  function setOpenInsuranceDetailsModal() {
    setInsuranceState({
      selectedPolicyType: null,
      selectedPolicyFundingSourceId: undefined,
      openInsuranceDetailsModal: !insuranceState.openInsuranceDetailsModal,
    });
  }

  const handleClickEditPolicy = (
    selectedPolicyType: FundingPolicyType | null,
    selectedPolicyFundingSourceId: string | undefined,
  ) => {
    setInsuranceState({
      selectedPolicyType: selectedPolicyType,
      openInsuranceDetailsModal: true,
      selectedPolicyFundingSourceId: selectedPolicyFundingSourceId,
    });
  };

  const handleClickEditSelfFunding = (fundingSourceId: string) => {
    setSelectedSelfFundingSourceId(fundingSourceId);
    setOpenSelfFundingModal(true);
  };

  const emptyStateAddOptions: EmptyStateAddOptionDef[] = [
    {
      title: 'Add Insurance',
      testId: 'empty-state-add-insurance',
      description: 'Upload a policy document or provide policy details to get started.',
      icon: PiSparkle,
      onClick: () => {
        const state = zipCodeToState(client.intakeSurvey.clientZipCode);
        if (
          isEligibleForShopInsurance &&
          INSURANCE_SHOPPING_CONFIG.allowedStates.has(state.stateAbbreviation)
        ) {
          setOpenAddInsuranceOptionsModal(true);
        } else {
          setOpenInsuranceDetailsModal();
        }
        setOpenSelectFundingSourceModal(false);
      },
    },
    {
      title: 'Allocate Savings/Investments',
      testId: 'empty-state-allocate-savings',
      description: 'Earmark personal funds for long-term care.',
      icon: MdOutlineSavings,
      onClick: () => {
        setSelectedSelfFundingSourceId(undefined);
        setOpenSelfFundingModal(true);
        setOpenSelectFundingSourceModal(false);
      },
    },
    {
      title: 'Add Annuities',
      testId: 'empty-state-add-annuities',
      description: 'Track income streams from annuities.',
      icon: Banknote,
      onClick: () => {
        setOpenAnnuityDetailsModal(true);
        setOpenSelectFundingSourceModal(false);
      },
    },
  ];

  const fundingSourceAdded =
    clientHasPolicyFunding(client) ||
    clientHasAnnuityFunding(client) ||
    clientHasSelfFunding(client);

  const showEmptyStateAddFundingSource = !fundingSourceAdded && !showGroupPolicy;

  const shouldNavigateToUnderwritingDashboard = Object.values(
    client.multipleFundingSources.ltcPolicy,
  )
    .map(ltcPolicy => {
      return isPolicyEligibleForApplication(ltcPolicy);
    })
    .some(hasSteps => hasSteps);

  // set onboarding to completed if the client has navigated to this page
  useEffect(() => {
    if (
      (onboardingSlideProgress && onboardingSlideProgress.hasClientCompletedOnboarding) ||
      sessionType !== SessionType.client
    ) {
      return;
    }
    const clientOnboardingSlideProgressId =
      onboardingSlideProgress?.clientOnboardingSlideProgressId ?? newUuid();
    const updatedClientTags = [...client.clientTags, CLIENT_COMPLETED_REVIEW];
    dispatch(
      putClientOnboardingSlideProgressByClientIdRequest({
        clientOnboardingSlideProgressId,
        clientId,
        hasClientCompletedOnboarding: true,
        clientTags: updatedClientTags,
      }),
    );
  }, [clientId, dispatch, sessionType]);

  return (
    <OnboardingChrome
      slidePage={OnboardingFundingSources}
      nextSlidePage={
        shouldNavigateToUnderwritingDashboard
          ? OnboardingPolicyApplicationDashboard
          : OnboardingFundingCoverage
      }
      nextButtonLabel={`Next`}
      disableNextButton={!fundingSourceAdded}
      errorModalMessage={
        showGroupPolicy
          ? 'Please select a benefit amount to continue'
          : 'Please add a funding source to continue'
      }
      citations={[citations.OnboardingInsuranceDetailsDisclaimer]}
    >
      <SlideContent
        headerText={`${clientFirstName}'s long-term care financing plan`}
        data-testid="funding-sources-header"
        headerAction={
          <div className="flex flex-row flex-wrap gap-2 md:flex-col md:flex-nowrap">
            {featureFlags.flags.ltcFinancialPlanning && (
              <Button
                variant="outline"
                onClick={() => navigate(`/clients/${clientId}/onboarding/ltc-financial-planning`)}
                data-testid="ltc-financial-planning-button"
                className="w-full [@media_(min-width:_480px)]:w-max"
              >
                Try Financial Planning
              </Button>
            )}
            {suggestDownloadReportParam && (
              <Button
                variant="outline"
                onClick={() => navigateToPrintReport(client.clientId)}
                data-testid="download-report-button"
                className="w-full [@media_(min-width:_480px)]:w-max"
              >
                Download Report
                <FileDown className="ml-2" />
              </Button>
            )}
          </div>
        }
      >
        <div className="mb-6 mt-2 flex flex-col gap-1 md:mt-8">
          <FundingSourceMetrics
            client={client}
            className="my-3"
          />

          <GroupInsurancePlanSelector
            className={`mb-6 mt-8`}
            setInsuranceState={setInsuranceState}
          />

          {/* Add funding source options */}
          {showEmptyStateAddFundingSource && (
            <EmptyStateAddButtons
              className="mt-6"
              title="Funding options"
              description={`Quickly add the ways ${clientFirstName} already has or plans
                to pay for long-term care.`}
              addOptions={emptyStateAddOptions}
            />
          )}

          {/* combined summary table */}
          {fundingSourceAdded && !showGroupPolicy && (
            <div data-testid="funding-table">
              <CustomizePlanButtonGroup
                client={client}
                className="ml-auto flex flex-row gap-1 md:my-5"
                useDefaultLayout={false}
                useIconOnlyOnMobile={true}
              />

              <FundingTable
                setOpenModal={{
                  ltcPolicy: handleClickEditPolicy,
                  annuity: setOpenAnnuityDetailsModal,
                  selfFunding: handleClickEditSelfFunding,
                }}
                handleShowDownloadReport={handleShowDownloadReport}
                className="mt-2"
                primaryButton={
                  <Button
                    className="w-full text-base md:flex md:w-48 md:items-center md:self-end"
                    onClick={() => setOpenSelectFundingSourceModal(true)}
                    data-testid="add-funding-button"
                  >
                    <GoPlus className="h-5 w-5" />
                    <span>Add funding</span>
                  </Button>
                }
              />
            </div>
          )}
        </div>

        <SelectFundingSourceModal
          open={openSelectFundingSourceModal}
          emptyStateAddOptions={emptyStateAddOptions}
          onClose={() => setOpenSelectFundingSourceModal(false)}
        />
        <AddInsuranceOptionsModal
          open={openAddInsuranceOptionsModal}
          onClose={() => setOpenAddInsuranceOptionsModal(false)}
          onAnalyzeExistingPolicy={() => {
            setOpenAddInsuranceOptionsModal(false);
            setOpenInsuranceDetailsModal();
          }}
          onShopNewInsurance={() => {
            setOpenAddInsuranceOptionsModal(false);
            handleOpenShopInsuranceModal();
          }}
          data-testid="shop-insurance-modal-open-button"
        />
        {isQueryReady && (
          <ShopInsuranceModal
            open={openShopInsuranceModal}
            onClose={handleCloseShopInsuranceModal}
            queryOptions={queryParam}
          />
        )}
        <AddPolicyModal
          open={insuranceState.openInsuranceDetailsModal}
          onClose={setOpenInsuranceDetailsModal}
          fundingSourceId={insuranceState.selectedPolicyFundingSourceId}
        />
        <AnnuityDetailsModal
          open={openAnnuityDetailsModal}
          setOpen={setOpenAnnuityDetailsModal}
        />
        <SelfFundingDetailsModal
          open={openSelfFundingModal}
          setOpen={setOpenSelfFundingModal}
          fundingSourceId={selectedSelfFundingSourceId}
        />
      </SlideContent>
      <ExplainSlideButton
        videoUrl=""
        articleUrl="https://help.waterlily.com/en/articles/9408213-building-and-modeling-a-funding-plan-for-your-client-s-long-term-care"
      />
    </OnboardingChrome>
  );
}

export function clientHasPolicyFunding(client: Client) {
  return Object.entries(client.multipleFundingSources.ltcPolicy).length > 0;
}

export function clientHasAnnuityFunding(client: Client) {
  return client.fundingSources.annuity.hasAnnuity;
}

export function clientHasSelfFunding(client: Client) {
  return Object.values(client.multipleFundingSources.selfFunding).some(selfFunding => {
    const areSelfFundingFieldsPopulated =
      (selfFunding.annualRateOfReturn && selfFunding.annualRateOfReturn > 0) ||
      (selfFunding.monthlyContributionAmount && selfFunding.monthlyContributionAmount > 0) ||
      (selfFunding.existingAssetsValue && selfFunding.existingAssetsValue > 0);

    return selfFunding.hasSelfFunding || areSelfFundingFieldsPopulated;
  });
}
